// Removed direct SendInviteModal import; will use ChatBar's modal via external trigger
// src/pages/ProfileScreen.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
    // ...existing code...
    // ...existing code...
import ChatBar from "./chatScreen/chatbar";
import { TextField } from "./common/inputFiled";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { ensureChatSocket, subscribeContactSync } from "../services/chatSocketManager";
import {
    applyContactListPatch,
    CONTACTS_CHANGED_EVENT,
    dispatchContactsChanged,
    extractContactFromSocketPayload,
    mergeContactRecord,
    sendFriendRequestWithRelations,
    shouldSendReciprocalFriendRequest,
} from "../utils/contactListSync";
import { appStorage } from "../utils/appStorage";

const promoteContactToTop = (contactsList, userId, fallbackContact = null) => {
    if (!userId) return contactsList;

    const list = Array.isArray(contactsList) ? [...contactsList] : [];
    const index = list.findIndex((c) => String(c.user_id) === String(userId));

    if (index >= 0) {
        const contact = fallbackContact
            ? { ...list[index], ...fallbackContact, user_id: list[index].user_id || userId }
            : list[index];

        if (index > 0) {
            list.splice(index, 1);
            return [contact, ...list];
        }

        list[index] = contact;
        return list;
    }

    if (fallbackContact) {
        return [{ ...fallbackContact, user_id: userId }, ...list];
    }

    return list;
};

export default function ProfileScreen () {
    const [profile, setProfile] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);

    const isFriendContact = useCallback((value) =>
        value === 1 || value === "1" || value === true || value === "true", []);

    const [contacts, setContacts] = useState([]);
    const [birthday, setBirthday] = useState([]);
    const [friendRes, setFriendRes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingRelationContact, setPendingRelationContact] = useState(null);
    const [pendingRelationAction, setPendingRelationAction] = useState("permission");
    const [showPendingRelationModal, setShowPendingRelationModal] = useState(false);
    const [selectedRelations, setSelectedRelations] = useState([]);
    const [isSubmittingRelation, setIsSubmittingRelation] = useState(false);
    const [isRejectingRelation, setIsRejectingRelation] = useState(false);
    const [prioritizeContactId, setPrioritizeContactId] = useState(null);
    const socketRef = useRef(null);
    const chatBarInviteRef = useRef(null);
    const contactRefreshTimerRef = useRef(null);

    const apiGet = useCallback(async (endpoint, token) => {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || `Failed to fetch ${endpoint}`);

        if (endpoint === "/birthdays/getall") {
            return data || {};
        }

        return data?.data?.[0] || {};
    }, []);

    const refreshContacts = useCallback(async (promoteUserId = null, fallbackContact = null) => {
        try {
            const token = Cookies.get("auth_token");
            if (!token) return;

            const [contactRes, friendReqRes] = await Promise.all([
                apiGet("/get-contacts", token),
                apiGet("/friend-request/list", token),
            ]);
            if (promoteUserId) {
                setPrioritizeContactId(String(promoteUserId));
            } else {
                setPrioritizeContactId(null);
            }

            const contactsList = promoteContactToTop(
                contactRes?.contacts || [],
                promoteUserId,
                fallbackContact
            );

            setContacts(contactsList);
            setFriendRes(friendReqRes || {});

            setSelectedContact((prev) => {
                if (!prev?.user_id) return prev;
                const updated = contactsList.find((c) => String(c.user_id) === String(prev.user_id));
                return updated || prev;
            });

            const pendingContact = contactsList.find(
                c => Number(c.is_friend || 0) === 1 && Number(c.pending_relation_selection || 0) === 1
            );
            const receivedRequests = Array.isArray(friendReqRes?.received_requests)
                ? friendReqRes.received_requests
                : [];

            if (pendingContact) {
                setPendingRelationContact(pendingContact);
                setPendingRelationAction("permission");
                setSelectedRelations([]);
                setShowPendingRelationModal(true);
            } else if (receivedRequests.length > 0) {
                setPendingRelationContact(receivedRequests[0]);
                setPendingRelationAction("accept_request");
                setSelectedRelations([]);
                setShowPendingRelationModal(true);
            } else {
                setShowPendingRelationModal(false);
                setPendingRelationContact(null);
                setPendingRelationAction("permission");
                setSelectedRelations([]);
            }
        } catch (error) {
            }
    }, [apiGet]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = Cookies.get("auth_token");

                if (!token) {
                    toast.dismiss();
                    toast.error("Session expired. Please log in again.");
                    return (window.location.href = "/login");
                }

                // 🚀 Run all APIs in parallel → much faster
                const [
                    profileRes,
                    contactRes,
                    birthdayRes,
                    friendReqRes,
                ] = await Promise.all([
                    apiGet("/profile", token),
                    apiGet("/get-contacts", token),
                    apiGet("/birthdays/getall", token),
                    apiGet("/friend-request/list", token),
                ]);
                const profileData = profileRes?.profile || {};
                setProfile(profileData);
                if (profileData.payment_status != null) {
                    appStorage.setItem("payment_status", profileData.payment_status.toString());
                }
                if (profileData.choose_plan_required != null) {
                    appStorage.setItem(
                        "choose_plan_required",
                        profileData.choose_plan_required.toString()
                    );
                }
                if (
                    profileData.payment_status === 0 ||
                    profileData.payment_status === "0" ||
                    profileData.choose_plan_required === 1 ||
                    profileData.choose_plan_required === "1"
                ) {
                    toast.dismiss();
                    toast("Please complete payment to continue.");
                    window.location.replace("/payment");
                    return;
                }
                const contactsList = contactRes?.contacts || [];
                setContacts(contactsList);

                const birthdays = Array.isArray(birthdayRes?.birthdays) ? birthdayRes.birthdays : [];
                const anniversaries = Array.isArray(birthdayRes?.anniversaries) ? birthdayRes.anniversaries : [];
                const combined = [...birthdays, ...anniversaries].map((item) => {
                    const rawUrl = item?.profile_image_url || item?.profile_image || "";
                    const avatar = rawUrl && rawUrl.startsWith("http")
                        ? rawUrl
                        : rawUrl
                            ? `${process.env.REACT_APP_API_BASE_URL}${rawUrl}`
                            : "";
                    const name = item?.full_name || `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
                    return {
                        name: name || "Unknown",
                        avatar,
                        type: item?.date_of_birth ? "birthday" : "anniversary",
                        original: item
                    };
                });

                setBirthday(combined);
                setFriendRes(friendReqRes || {});

                // Check for pending relation selection
                const pendingContact = contactsList.find(
                    c => Number(c.is_friend || 0) === 1 && Number(c.pending_relation_selection || 0) === 1
                );
                const receivedRequests = Array.isArray(friendReqRes?.received_requests)
                    ? friendReqRes.received_requests
                    : [];

                if (pendingContact) {
                    setPendingRelationContact(pendingContact);
                    setPendingRelationAction("permission");
                    setShowPendingRelationModal(true);
                } else if (receivedRequests.length > 0) {
                    setPendingRelationContact(receivedRequests[0]);
                    setPendingRelationAction("accept_request");
                    setShowPendingRelationModal(true);
                }
            } catch (err) {
                toast.dismiss();
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [apiGet]);

    // Keep socket alive + live contact sync (socket events + polling fallback)
    useEffect(() => {
        if (!profile?.user_id) return;

        const token = Cookies.get("auth_token");
        if (!token) return;

        socketRef.current = ensureChatSocket(token);

        const scheduleRefresh = () => {
            if (contactRefreshTimerRef.current) clearTimeout(contactRefreshTimerRef.current);
            contactRefreshTimerRef.current = setTimeout(() => {
                refreshContacts();
                contactRefreshTimerRef.current = null;
            }, 300);
        };

        const unsubscribe = subscribeContactSync(({ type, payload, eventName }) => {
            if (type === "socket" && payload) {
                const contact = extractContactFromSocketPayload(payload);
                if (contact?.user_id) {
                    setContacts((prev) => applyContactListPatch(prev, contact));
                    setSelectedContact((prev) => {
                        if (!prev || String(prev.user_id) !== String(contact.user_id)) return prev;
                        return mergeContactRecord(prev, contact);
                    });
                }
            }
            if (type === "socket" || type === "refresh") {
                scheduleRefresh();
            }
        });

        const onContactsChanged = () => scheduleRefresh();
        window.addEventListener(CONTACTS_CHANGED_EVENT, onContactsChanged);

        return () => {
            unsubscribe();
            window.removeEventListener(CONTACTS_CHANGED_EVENT, onContactsChanged);
            if (contactRefreshTimerRef.current) {
                clearTimeout(contactRefreshTimerRef.current);
                contactRefreshTimerRef.current = null;
            }
        };
    }, [profile?.user_id, refreshContacts]);

    // Poll contacts while this page is open — server does not push friend accept/remove on socket
    useEffect(() => {
        if (!profile?.user_id) return;

        const pollContacts = () => {
            if (document.hidden) return;
            refreshContacts();
        };

        pollContacts();
        const intervalId = setInterval(pollContacts, 8000);
        const onFocus = () => pollContacts();
        const onVisible = () => {
            if (!document.hidden) pollContacts();
        };

        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [profile?.user_id, refreshContacts]);

    // Handle pending relation selection
    const relationOptions = ["Friend", "Family", "Work", "Other"];

    const handleSubmitRelations = async () => {
        if (!pendingRelationContact || selectedRelations.length === 0) {
            toast.error("Please select at least one relation");
            return;
        }

        setIsSubmittingRelation(true);
        try {
            const token = Cookies.get("auth_token");
            const relationsToSend = [...selectedRelations];

            let res;
            if (pendingRelationAction === "accept_request") {
                const payload = new URLSearchParams({
                    user_id: pendingRelationContact.user_id,
                    action: "1",
                });
                payload.append("relations", relationsToSend.join(","));

                res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/accept_reject_friend_request`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Bearer ${token}`
                    },
                    body: payload.toString()
                });
            } else {
                const formData = new FormData();
                formData.append("user_id", pendingRelationContact.user_id);
                formData.append("relations", relationsToSend);

                res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-profilepermission/set`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: formData
                });
            }

            const data = await res.json();
            if (data.status === "1") {
                const promotedUserId = pendingRelationContact.user_id;

                if (
                    pendingRelationAction === "accept_request" &&
                    shouldSendReciprocalFriendRequest(
                        contacts,
                        friendRes?.received_requests
                    )
                ) {
                    const sendResult = await sendFriendRequestWithRelations(
                        token,
                        promotedUserId,
                        relationsToSend
                    );
                    if (!sendResult.ok) {
                        toast.error(sendResult.message || "Failed to send reciprocal friend request");
                    } else {
                        dispatchContactsChanged({
                            userId: promotedUserId,
                            action: "friend_request_sent",
                        });
                    }
                }

                toast.success(
                    pendingRelationAction === "accept_request"
                        ? "Friend request accepted successfully!"
                        : "Relations updated successfully!"
                );

                const existingContact =
                    contacts.find((c) => String(c.user_id) === String(promotedUserId)) || {};

                const promotedContact = {
                    ...existingContact,
                    ...pendingRelationContact,
                    is_friend: 1,
                    pending_relation_selection: 0,
                    request_send: 0,
                    friend_request_received: 0,
                    relations: relationsToSend.join(","),
                    active_relations: relationsToSend.join(","),
                };

                setContacts((prev) => promoteContactToTop(prev, promotedUserId, promotedContact));
                setShowPendingRelationModal(false);
                setPendingRelationContact(null);
                setSelectedRelations([]);

                await refreshContacts(promotedUserId, promotedContact);
                dispatchContactsChanged({
                    userId: promotedUserId,
                    action: "friend_accepted",
                });
            } else {
                toast.error(data.message || "Failed to update relations");
            }
        } catch (error) {
            toast.error("Error updating relations");
            } finally {
            setIsSubmittingRelation(false);
        }
    };

    const handleRejectRelations = async () => {
        if (!pendingRelationContact || pendingRelationAction !== "accept_request") return;

        setIsRejectingRelation(true);
        try {
            const token = Cookies.get("auth_token");
            const payload = new URLSearchParams({
                user_id: pendingRelationContact.user_id,
                action: "2",
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/accept_reject_friend_request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`
                },
                body: payload.toString()
            });

            const data = await res.json();
            if (data.status === "1") {
                toast.success(data.message || "Friend request rejected");
                await refreshContacts();
            } else {
                toast.error(data.message || "Failed to reject request");
            }
        } catch (error) {
            toast.error("Error rejecting request");
            } finally {
            setIsRejectingRelation(false);
        }
    };

    const infoFields = [
        { label: "First Name", value: profile?.first_name, startIcon: "../input-logos/user.svg", type: "text" },
        { label: "Last Name", value: profile?.last_name, startIcon: "../input-logos/user.svg", type: "text" },
        { label: "Personal Email", value: profile?.email, startIcon: "./input-logos/sms.svg", type: "email" },
        { label: "Work Email", value: profile?.work_email, startIcon: "./input-logos/sms.svg", type: "email" },
        { label: "Home Fixed", value: profile?.home_fixed, type: "number" },
        { label: "Work Fixed", value: profile?.work_fixed, type: "number" },
        { label: "Date of Birth", value: profile?.dob, startIcon: "../input-logos/calander.svg", type: "date" },
    ];

    const visibleFields = infoFields.filter((field) => {
        const v = field.value;
        if (v === null || v === undefined) return false;
        return String(v).trim() !== "";
    });

    const redirectToChat = useCallback((userId, phone) => {
        // Navigate to chat with contact ID as route parameter
        window.location.href = `/chat/${userId}`;
    }, []);
    // Reference for ChatBar's external invite modal trigger

    // Handler to open invite modal from profile card (non-PhoneBook users)
    const handleOpenInviteModal = useCallback(() => {
        if (chatBarInviteRef.current?.openInvite) {
            chatBarInviteRef.current.openInvite(selectedContact);
        }
    }, [selectedContact]);

    const handleOpenFriendRequestModal = useCallback(() => {
        if (chatBarInviteRef.current?.openFriendRequest) {
            chatBarInviteRef.current.openFriendRequest(selectedContact);
            return;
        }
        toast.error("Unable to open friend request. Please try again.");
    }, [selectedContact]);

    const isPhoneBookUser = useCallback(
        (contact) => Number(contact?.phone_book_user ?? 0) === 1,
        []
    );

    const handleSelectContact = useCallback((contact) => {
        if (contact && typeof contact === "object" && !isFriendContact(contact.is_friend)) {
            setSelectedContact(prev => {
                if (prev && String(prev.user_id) === String(contact.user_id)) return prev;
                return contact;
            });
        }
    }, [isFriendContact]);

    const setExternalInviteRef = useCallback((ref) => {
        chatBarInviteRef.current = ref;
    }, []);

    const toggleRelationSelection = useCallback((relation) => {
        setSelectedRelations((prev) => {
            const isSelected = prev.includes(relation);

            if (relation === "Other") {
                return isSelected ? [] : ["Other"];
            }

            const withoutOther = prev.filter((item) => item !== "Other");
            return isSelected
                ? withoutOther.filter((item) => item !== relation)
                : [...withoutOther, relation];
        });
    }, []);

    if (loading)
        return (
            <div className="h-full min-h-0 overflow-hidden bg-[#F3F0FF]">
                <div className="h-full min-h-0 rounded-2xl shadow-lg overflow-hidden">
                    <div className="flex h-full min-h-0">
                            <div className="w-full md:w-[360px] md:min-w-[300px] md:flex-shrink-0 h-full min-h-0">
                            <ChatBar loading />
                        </div>
                        <section className="w-full md:flex-1 min-w-0 p-4 md:p-6 bg-[#F3F0FF] overflow-y-auto h-full min-h-0">
                            <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
                                <div className="h-6 w-40 bg-gray-200 rounded" />
                                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                                    <div className="flex justify-center md:block">
                                        <div className="h-24 w-24 md:h-36 md:w-36 rounded-full bg-gray-200" />
                                    </div>
                                    <div className="w-full space-y-3">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div key={idx} className="h-12 rounded-xl bg-gray-200" />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        );

    return (
        <div className="h-full min-h-0 overflow-hidden bg-[#F3F0FF]">
            {/* Pending Relation Modal - Forced, cannot close */}
            {showPendingRelationModal && pendingRelationContact && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-[90%] max-w-sm">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">
                            Please select how do you know {pendingRelationContact.full_name || pendingRelationContact.name}
                        </h2>

                        <div className="space-y-3 mb-6">
                            {relationOptions.map((relation) => {
                                const isSelected = selectedRelations.includes(relation);
                                return (
                                    <div
                                        key={relation}
                                        onClick={() => toggleRelationSelection(relation)}
                                        className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg gap-3 ${
                                            isSelected
                                                ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                                : "bg-[#797C7B] bg-opacity-[11%] border-gray-200"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <p className="font-semibold text-sm text-gray-900">
                                                {relation}
                                            </p>
                                        </div>

                                        <div
                                            className={`w-6 h-6 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${
                                                isSelected
                                                    ? "bg-[#7C4DFF] border-[#7C4DFF]"
                                                    : "border-gray-400"
                                            }`}
                                        >
                                            {isSelected && (
                                                <img
                                                    src="../chatscreen/radiobutton.svg"
                                                    alt="Selected"
                                                    className="w-6 h-6"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={`flex gap-3 ${pendingRelationAction === "accept_request" ? "" : ""}`}>
                            {pendingRelationAction === "accept_request" && (
                                <button
                                    type="button"
                                    className="flex-1 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleRejectRelations}
                                    disabled={isRejectingRelation || isSubmittingRelation}
                                >
                                    {isRejectingRelation ? "Rejecting..." : "Reject"}
                                </button>
                            )}
                            <button
                                type="button"
                                className={`${pendingRelationAction === "accept_request" ? "flex-1" : "w-full"} submit-button disabled:opacity-50 disabled:cursor-not-allowed`}
                                onClick={handleSubmitRelations}
                                disabled={selectedRelations.length === 0 || isSubmittingRelation || isRejectingRelation}
                            >
                                {isSubmittingRelation ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-3">
                            You must select at least one relation to continue
                        </p>
                    </div>
                </div>
            )}

            <div className="h-full min-h-0 rounded-2xl shadow-lg overflow-hidden">
                <div className="flex h-full min-h-0">
                    {/* Sidebar - Mobile: Show/Hide toggle, Desktop: Always visible */}
                        <div className={`block w-full md:w-[360px] md:min-w-[300px] md:flex-shrink-0 h-full min-h-0`}>
                        <ChatBar
                            contacts={contacts}
                            bdayData={birthday}
                            friendResData={friendRes}
                            disableAutoRefresh
                            redirectToChat={redirectToChat}
                            onRefreshContacts={refreshContacts}
                            onSelectContact={handleSelectContact}
                            externalInviteTrigger={setExternalInviteRef}
                            prioritizeUserId={prioritizeContactId}
                        />
                    </div>

                    {/* Profile Section - Mobile: Full width when sidebar hidden, Desktop: 70% */}
                        <section className={`hidden md:block w-full md:flex-1 p-4 md:p-6 bg-[#F3F0FF] overflow-y-auto h-full min-h-0`}>
                        {/* Mobile Menu Toggle Button */}
                        {/* <div className="md:hidden mb-4">
                            <button
                                onClick={() => setShowSidebar(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                <span className="font-medium">Contacts</span>
                            </button>
                        </div> */}

                        <div className="max-w-4xl mx-auto">
                            {selectedContact && !isFriendContact(selectedContact.is_friend) ? (
                                <div className="w-full min-h-[80vh] flex items-center justify-center py-8">
                                    <div className="relative w-full max-w-lg mx-auto bg-white rounded-3xl shadow-2xl flex flex-col  overflow-hidden animate-fadeIn border border-gray-200">
                                        {/* Left: Profile Image */}
                                        <div className="flex flex-col items-center justify-center bg-gradient-to-br from-[#7C4DFF] to-[#B39DDB]  w-full py-12 px-6 md:px-8">
                                            <div className="h-40 w-40 rounded-full overflow-hidden border-8 border-white shadow-lg mb-6">
                                                <img
                                                    src={
                                                        selectedContact.profile_image
                                                            ? `${process.env.REACT_APP_API_BASE_URL}${selectedContact.profile_image}`
                                                            : "./chatscreen/Ellipse1.svg"
                                                    }
                                                    alt="User"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-white mb-1">{selectedContact.full_name || selectedContact.name || "Not Provided"}</div>
                                                <div className="text-md text-white/80">{selectedContact.email || "No email"}</div>
                                            </div>
                                        </div>
                                        {/* Right: Details & Actions */}
                                        <div className="flex-1 flex flex-col justify-center px-8 py-10 md:py-16 bg-white">
                                            <div className="space-y-6">
                                                <div>
                                                    <div className="text-gray-500 text-xs font-semibold mb-1">Mobile No</div>
                                                    <div className={selectedContact.phone_number ? "text-xl font-semibold text-gray-900" : "text-xl font-semibold text-red-500"}>
                                                        {selectedContact.country_code ? `${selectedContact.country_code} ` : ""}
                                                        {selectedContact.phone_number || "Not Provided"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-gray-500 text-xs font-semibold mb-1">Email</div>
                                                    <div className={selectedContact.email ? "text-lg text-gray-800" : "text-lg text-yellow-500"}>
                                                        {selectedContact.email || "Not Provided"}
                                                    </div>
                                                </div>
                                                {/* Add more fields as needed */}
                                            </div>
                                            <div className="mt-10 flex flex-col gap-4">
                                                {selectedContact.request_send ? (
                                                    <button className="w-full py-3 rounded-xl bg-gray-300 text-gray-700 font-semibold cursor-not-allowed text-lg shadow" disabled>
                                                        Friend Request Sent
                                                    </button>
                                                ) : isPhoneBookUser(selectedContact) ? (
                                                    <button
                                                        className="submit-button w-full py-3 text-lg"
                                                        onClick={handleOpenFriendRequestModal}
                                                    >
                                                        Send Friend Request
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="submit-button w-full py-3 text-lg"
                                                        onClick={() => {
                                                            if (!selectedContact.phone_number) {
                                                                toast.error("Mobile number required to share invitation link.");
                                                                return;
                                                            }
                                                            handleOpenInviteModal();
                                                        }}
                                                        disabled={!selectedContact.phone_number}
                                                    >
                                                        Share Invitation Link
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
                                        Personal Info
                                    </h2>
                                    <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                                        <div className="flex justify-center md:block">
                                            <div className="h-24 w-24 md:h-36 md:w-36 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                                                <img
                                                    src={
                                                        profile?.profile_image
                                                            ? `${process.env.REACT_APP_API_BASE_URL}${profile.profile_image}`
                                                            : "./chatscreen/Ellipse1.svg"
                                                    }
                                                    alt="User"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full space-y-3">
                                            {visibleFields.length === 0 ? (
                                                Array.from({ length: 6 }).map((_, idx) => (
                                                    <div key={idx} className="h-12 rounded-xl bg-gray-200 animate-pulse" />
                                                ))
                                            ) : (
                                                visibleFields.map((field) => (
                                                    <TextField
                                                        key={field.label}
                                                        label={field.label}
                                                        type={field.type || "text"}
                                                        defaultValue={field.value}
                                                        startIcon={field.startIcon}
                                                        disabled
                                                    />
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
